import React, { useState, useContext, useMemo, useEffect, useLayoutEffect } from 'react';
import { AppContext, supabase } from '../contexts/AppContext';
import { Cancha, Deporte, Socio, Turno, Rol, MedioDePago, ParticipanteInput, DeudaInput } from '../types';
import Modal from '../components/Modal';
import { useUI } from '../contexts/UIContext';

// Input para jugadores de Fútbol: autocomplete contra socios + caída a nombre libre.
// - Si la slot tiene socio_id: muestra el nombre del socio con badge y botón de desvincular.
// - Si no: input de texto. Al tipear muestra socios que coinciden y un atajo "Crear socio".
const JugadorFutbolRow: React.FC<{
    p: ParticipanteInput;
    idx: number;
    socios: Socio[];
    excludedIds: Set<string>;
    onChange: (next: ParticipanteInput) => void;
    onCrearSocio: (preName: string) => void;
}> = ({ p, idx, socios, excludedIds, onChange, onCrearSocio }) => {
    const [focused, setFocused] = useState(false);
    const linkedSocio = p.socio_id ? socios.find(s => String(s.id) === String(p.socio_id)) : null;

    const matches = useMemo(() => {
        if (linkedSocio) return [];
        const q = (p.nombre_libre || '').trim().toLowerCase();
        if (q.length < 1) return [];
        return socios
            .filter(s => !excludedIds.has(String(s.id)))
            .filter(s => `${s.nombre} ${s.apellido || ''}`.toLowerCase().includes(q))
            .slice(0, 5);
    }, [p.nombre_libre, socios, excludedIds, linkedSocio]);

    if (linkedSocio) {
        return (
            <div className="flex items-center gap-1">
                <div className="flex-1 p-2 border border-blue-300 rounded text-sm bg-blue-50 flex items-center gap-2 min-w-0">
                    <span className="truncate flex-1">{linkedSocio.nombre} {linkedSocio.apellido}</span>
                    <span className="text-[10px] uppercase font-bold bg-blue-200 text-blue-800 px-1.5 py-0.5 rounded">socio</span>
                </div>
                <button
                    type="button"
                    onClick={() => onChange({ nombre_libre: `${linkedSocio.nombre} ${linkedSocio.apellido || ''}`.trim() })}
                    className="text-gray-400 hover:text-gray-700 px-2 text-lg"
                    title="Desvincular del socio (queda como nombre libre)"
                >×</button>
            </div>
        );
    }

    const trimmed = (p.nombre_libre || '').trim();
    const exactMatch = matches.find(s => `${s.nombre} ${s.apellido || ''}`.trim().toLowerCase() === trimmed.toLowerCase());
    const showCrear = focused && trimmed.length >= 2 && !exactMatch;

    return (
        <div className="relative">
            <input
                type="text"
                placeholder={`Jugador ${idx + 2}`}
                value={p.nombre_libre || ''}
                onChange={e => onChange({ nombre_libre: e.target.value })}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => setFocused(false), 150)}
                className="p-2 border rounded text-sm w-full"
                autoComplete="off"
            />
            {focused && (matches.length > 0 || showCrear) && (
                <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border rounded shadow-lg max-h-56 overflow-y-auto text-sm">
                    {matches.map(s => (
                        <button
                            key={s.id}
                            type="button"
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => onChange({ socio_id: s.id })}
                            className="block w-full text-left px-3 py-2 hover:bg-blue-50"
                        >
                            <span className="font-medium">{s.nombre} {s.apellido}</span>
                            <span className="text-xs text-gray-500 ml-1">— vincular como socio</span>
                        </button>
                    ))}
                    {showCrear && (
                        <button
                            type="button"
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => onCrearSocio(trimmed)}
                            className="block w-full text-left px-3 py-2 hover:bg-green-50 border-t text-green-700 font-medium"
                        >
                            + Crear socio "{trimmed}"
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

const TurnosScreen: React.FC = () => {
    const {
        user,
        canchas, addCancha, updateCancha, turnos, addTurno, addTurnoRecurrencia, updateTurno, updateTurnoRecurrenciaRecord, updateTurnoRecurrencia, deleteTurno,
        socios, addSocio, isReadOnly,
        sesionCaja, registrarVenta, productos, addProducto,
        fetchClubData,
        reglasPuntos,
        addDeudas,
        pendingOpenTurnoId, clearPendingOpenTurnoId
    } = useContext(AppContext);
    const ui = useUI();

    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [isCanchaModalOpen, setIsCanchaModalOpen] = useState(false);
    const [isTurnoModalOpen, setIsTurnoModalOpen] = useState(false);
    const [isDuracionCustomizada, setIsDuracionCustomizada] = useState(false); // Controla si se usa duración personalizada
    
    // Estado para manejar el borrado
    const [turnoToDelete, setTurnoToDelete] = useState<Turno | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDeleteChoiceOpen, setIsDeleteChoiceOpen] = useState(false);

    // Estados para crear Cancha
    const [newCancha, setNewCancha] = useState<Partial<Cancha>>({ 
        nombre: '', deporte: 'Padel', precio_base: 0, duracion_minutos: 60, estado: 'activa' 
    });

    // Estado para editar cancha (si tiene id, se interpreta como edición)
    const [editingCanchaId, setEditingCanchaId] = useState<number | null>(null);

    // Estado para "Editando" vs "Creando"
    const [editingTurnoId, setEditingTurnoId] = useState<number | null>(null);

    // Estados para crear/editar Turno
    const [newTurno, setNewTurno] = useState<{
        canchaId: number | null;
        horaInicio: string;
        socioTitularId: string | number | null;
        precio: number;
        notas: string;
        estado: Turno['estado'];
        participantes: ParticipanteInput[];
        medioPago: MedioDePago;
        duracionMinutos: number; // Duración personalizada del turno
        isFijo: boolean;          // Si es un turno recurrente (recurrencia semanal)
    }>({
        canchaId: null,
        horaInicio: '10:00',
        socioTitularId: null,
        precio: 0,
        notas: '',
        estado: 'reservado',
        participantes: [],
        medioPago: MedioDePago.EFECTIVO,
        duracionMinutos: 60, // Por defecto 1 hora
        isFijo: false
    });

    // Cantidad de jugadores para canchas de Fútbol (titular + N-1 adicionales).
    const [futbolCantidad, setFutbolCantidad] = useState<12 | 16>(12);

    // Estado para "Crear socio inline"
    const [isCreatingSocioInline, setIsCreatingSocioInline] = useState(false);
    const [inlineSocioName, setInlineSocioName] = useState('');
    const [inlineSocioPhone, setInlineSocioPhone] = useState('');
    const [inlineSocioDni, setInlineSocioDni] = useState('');
    const [inlineSocioEmail, setInlineSocioEmail] = useState('');
    const [inlineSocioTarget, setInlineSocioTarget] = useState<'titular' | number>('titular'); // 'titular' o índice del array de participantes

    // --- LOGICA DE CALENDARIO ---
    // Slots horarios visibles en la grilla: desde 9:00 hasta las 23:00.
    // El último slot (23:00) representa la franja 23:00–24:00 (medianoche).
    const hours = Array.from({ length: 15 }, (_, i) => i + 9); // 9:00 a 23:00 (medianoche)
    
    const turnosDelDia = useMemo(() => {
        return turnos.filter(t => t.fecha_inicio.startsWith(selectedDate));
    }, [turnos, selectedDate]);

    const parseISOToLocalDate = (iso: string) => {
        try {
            return new Date(iso);
        } catch (e) {
            return new Date(0);
        }
    };

    const getTurnoEnCelda = (canchaId: number, hour: number) => {
        // Buscamos un turno que solape con la celda horario [hour, hour+1) usando fechas locales
        return turnosDelDia.find(t => {
            if (t.cancha_id !== canchaId) return false;
            const start = parseISOToLocalDate(t.fecha_inicio);
            const end = parseISOToLocalDate(t.fecha_fin);

            const slotStart = new Date(`${selectedDate}T${String(hour).padStart(2, '0')}:00:00`);
            const slotEnd = new Date(slotStart.getTime() + 60 * 60000);
            return start < slotEnd && end > slotStart;
        });
        
    };

    // Handler moved out of the find to avoid leaking it into getTurnoEnCelda
    const handleOpenTurno = (cancha: Cancha, hour: number, e?: React.MouseEvent) => {
        setEditingTurnoId(null); // Modo Crear

        // Determine minute offset if click event provided (support :00 / :30)
        let minute = 0;
        if (e) {
            const target = e.currentTarget as HTMLElement;
            const rect = target.getBoundingClientRect();
            const clickY = e.clientY - rect.top;
            // Use the actual cell height (rect.height) to compute minutes within the hour
            const rawMinutes = Math.floor((clickY / Math.max(1, rect.height)) * 60);
            // Snap: top half -> :00, bottom half -> :30
            minute = rawMinutes < 30 ? 0 : 30;
        }

        const horaStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2,'0')}`;

        // Prevent creating a turno that would overlap existing ones on the same cancha
        const desiredStart = new Date(`${selectedDate}T${String(hour).padStart(2,'0')}:${String(minute).padStart(2,'0')}:00`);
        const desiredEnd = new Date(desiredStart.getTime() + cancha.duracion_minutos * 60000);
        const overlaps = turnosDelDia.some(t => {
            if (t.cancha_id !== cancha.id) return false;
            const s = parseISOToLocalDate(t.fecha_inicio);
            const f = parseISOToLocalDate(t.fecha_fin);
            return s < desiredEnd && f > desiredStart;
        });

        if (overlaps) {
            // Do not open modal if the desired start would overlap an existing turno
            // (This prevents selecting 11:00 when a 10:00-11:30 turno exists)
            return;
        }

        setNewTurno({
            canchaId: cancha.id,
            horaInicio: horaStr,
            socioTitularId: null,
            precio: cancha.precio_base,
            notas: '',
            estado: 'reservado',
            participantes: buildInitialParticipantes(cancha.deporte, 12),
            medioPago: MedioDePago.EFECTIVO,
            duracionMinutos: cancha.duracion_minutos, // Usar la duración por defecto de la cancha
            isFijo: false
        });
        if (cancha.deporte === 'Futbol') setFutbolCantidad(12);
        setIsDuracionCustomizada(false);
        setIsTurnoModalOpen(true);
    };

    // Construye el array inicial de participantes según el deporte.
    // Padel: 3 slots vacíos para socios (titular + 3 = 4 jugadores).
    // Fútbol: (cantidad - 1) slots vacíos para nombres libres (titular + N-1).
    // Otros: sin slots.
    function buildInitialParticipantes(deporte: Deporte | undefined, futbolN: number): ParticipanteInput[] {
        if (deporte === 'Padel') return [{ socio_id: 0 }, { socio_id: 0 }, { socio_id: 0 }];
        if (deporte === 'Futbol') return Array.from({ length: Math.max(0, futbolN - 1) }, () => ({ nombre_libre: '' }));
        return [];
    }

    // Medidas para render absoluto de bloques
    const gridRef = React.useRef<HTMLDivElement | null>(null);
    const [rowHeight, setRowHeight] = useState<number>(80);
    const [measuredRowHeight, setMeasuredRowHeight] = useState<number>(80);
    const [measurementReady, setMeasurementReady] = useState<boolean>(false);
    const measureTimeoutRef = React.useRef<any>(null);
    const [headerHeight, setHeaderHeight] = useState<number>(0);
    const [leftColWidth, setLeftColWidth] = useState<number>(64);
    const [columnWidth, setColumnWidth] = useState<number>(150);
    // Hover helper for reserve time preview (shows HH:00 or HH:30 while hovering cells)
    const [hoverReserve, setHoverReserve] = useState<{canchaId: number | null; hour: number | null; label: string | null}>({ canchaId: null, hour: null, label: null });

    const measureGrid = () => {
        const grid = gridRef.current;
        if (!grid) return;
        const headerEl = grid.querySelector('.canchas-header') as HTMLElement | null;
        const firstRow = grid.querySelector('.hour-row') as HTMLElement | null;
        const hourCol = grid.querySelector('.hour-col') as HTMLElement | null;
        if (headerEl) setHeaderHeight(headerEl.getBoundingClientRect().height);
        if (firstRow) {
            const measured = firstRow.getBoundingClientRect().height;
            setMeasuredRowHeight(measured);
            setRowHeight(Math.min(Math.max(measured, 60), 80));
        }
        if (hourCol) setLeftColWidth(hourCol.getBoundingClientRect().width);
        const totalWidth = grid.clientWidth;
        const cols = canchas.length || 1;
        const cw = Math.max(140, (totalWidth - (hourCol ? hourCol.getBoundingClientRect().width : 64)) / cols);
        setColumnWidth(cw);
    };

    // Measure synchronously before paint so overlays are positioned correctly on first render
    useLayoutEffect(() => {
        measureGrid();
    }, [canchas.length]);

    // Keep listening for resize changes and observe layout changes using ResizeObserver.
    useEffect(() => {
        window.addEventListener('resize', measureGrid);
        const grid = gridRef.current;
        let ro: ResizeObserver | null = null;
        if (grid && typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(() => {
                measureGrid();
                // debounce setting ready so layout can stabilize
                if (measureTimeoutRef.current) clearTimeout(measureTimeoutRef.current);
                measureTimeoutRef.current = setTimeout(() => setMeasurementReady(true), 60);
            });
            ro.observe(grid);
        }

        // initial measurement and mark ready after a short delay
        measureGrid();
        if (measureTimeoutRef.current) clearTimeout(measureTimeoutRef.current);
        measureTimeoutRef.current = setTimeout(() => setMeasurementReady(true), 80);

        return () => {
            window.removeEventListener('resize', measureGrid);
            if (ro) ro.disconnect();
            if (measureTimeoutRef.current) clearTimeout(measureTimeoutRef.current);
        };
    }, [canchas.length, turnosDelDia.length]);

    // --- LOGICA DE FILTRADO DE SOCIOS (Evitar duplicados) ---
    const selectedIdsSet = useMemo(() => {
        const set = new Set<string>();
        if (newTurno.socioTitularId) set.add(String(newTurno.socioTitularId));
        newTurno.participantes.forEach(p => {
            const sid = p.socio_id;
            if (sid && sid !== 0 && String(sid) !== '0') set.add(String(sid));
        });
        return set;
    }, [newTurno.socioTitularId, newTurno.participantes]);

    const getSociosDisponibles = (currentValue: string | number | null) => {
        return socios.filter(s => {
            const idStr = String(s.id);
            // Un socio está disponible si:
            // 1. NO está en el set de seleccionados globalmente
            // 2. O SI es el valor seleccionado actualmente en este dropdown (para que se pueda ver lo que elegiste)
            const isSelectedElsewhere = selectedIdsSet.has(idStr);
            const isCurrentValue = idStr === String(currentValue);
            return !isSelectedElsewhere || isCurrentValue;
        });
    };

    // --- MANEJADORES ---

    const handleSaveCancha = async () => {
        if(!newCancha.nombre || !newCancha.precio_base) {
            ui.showModal('Error', 'Completá nombre y precio');
            return;
        }
        if (newCancha.id) {
            // Edición
            const success = await updateCancha(newCancha as Cancha);
            if (!success) {
                ui.showModal('Error', 'No se pudo actualizar la cancha.');
                return;
            }
        } else {
            const success = await addCancha(newCancha as Omit<Cancha, 'id' | 'club_id'>);
            if (!success) {
                ui.showModal('Error', 'No se pudo crear la cancha.');
                return;
            }
        }
        setIsCanchaModalOpen(false);
        setNewCancha({ nombre: '', deporte: 'Padel', precio_base: 0, duracion_minutos: 60, estado: 'activa' });
        setEditingCanchaId(null);
    };

    const handleOpenTurnoModal = (cancha: Cancha, hour: number) => {
        setEditingTurnoId(null); // Modo Crear
        setNewTurno({
            canchaId: cancha.id,
            horaInicio: `${hour.toString().padStart(2, '0')}:00`,
            socioTitularId: null,
            precio: cancha.precio_base,
            notas: '',
            estado: 'reservado',
            participantes: buildInitialParticipantes(cancha.deporte, 12),
            medioPago: MedioDePago.EFECTIVO,
            duracionMinutos: cancha.duracion_minutos
            , isFijo: false
        });
        if (cancha.deporte === 'Futbol') setFutbolCantidad(12);
        setIsDuracionCustomizada(false);
        setIsTurnoModalOpen(true);
    };

    const handleEditTurno = (turno: Turno) => {
        setEditingTurnoId(turno.id); // Modo Editar
        const fecha = new Date(turno.fecha_inicio);
        const horaInicio = `${fecha.getHours().toString().padStart(2, '0')}:${fecha.getMinutes().toString().padStart(2, '0')}`;
        
        // Calcular la duración del turno existente
        const fechaFin = new Date(turno.fecha_fin);
        const duracionCalculada = Math.round((fechaFin.getTime() - fecha.getTime()) / 60000); // convertir ms a minutos
        
        // Mapear participantes existentes a formato del form
        let partsForm: ParticipanteInput[] = turno.participantes
            ? turno.participantes.map(p => p.socio_id
                ? { socio_id: p.socio_id }
                : { nombre_libre: p.nombre_libre || '' })
            : [];
        if (turno.cancha?.deporte === 'Padel') {
            while (partsForm.length < 3) partsForm.push({ socio_id: 0 });
        } else if (turno.cancha?.deporte === 'Futbol') {
            // Determinar 12 o 16 según el tamaño guardado (titular + N-1 = total). Default 12.
            const total = partsForm.length + 1;
            const detected: 12 | 16 = total > 12 ? 16 : 12;
            setFutbolCantidad(detected);
            while (partsForm.length < detected - 1) partsForm.push({ nombre_libre: '' });
        }

        setNewTurno({
            canchaId: turno.cancha_id,
            horaInicio: horaInicio,
            socioTitularId: turno.socio_titular_id,
            precio: turno.precio,
            notas: turno.notas || '',
            estado: turno.estado,
            participantes: partsForm,
            medioPago: MedioDePago.EFECTIVO,
            duracionMinutos: duracionCalculada,
            isFijo: turno.notas ? String(turno.notas).includes('[FIJO]') : false
        });
        // Determinar si es personalizada (no es 60 ni 90)
        setIsDuracionCustomizada(duracionCalculada !== 60 && duracionCalculada !== 90);
        setIsTurnoModalOpen(true);
    };

    // Si llegamos a esta pantalla con un turno pendiente para abrir (desde Caja), lo abrimos.
    useEffect(() => {
        if (!pendingOpenTurnoId) return;
        const turno = turnos.find(t => t.id === pendingOpenTurnoId);
        if (turno) {
            // Cambiar la fecha visible para que el turno esté en la grilla.
            const fecha = String(turno.fecha_inicio).split('T')[0];
            if (fecha) setSelectedDate(fecha);
            handleEditTurno(turno);
            clearPendingOpenTurnoId && clearPendingOpenTurnoId();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingOpenTurnoId, turnos]);


    // --- COBRO POR JUGADOR (sub-modal cuando se pasa un turno a 'Pagado') ---
    type JugadorCobro = {
        key: string;          // id estable: 'titular' o `p-{idx}`
        nombre: string;       // para mostrar
        socio_id: string | number | null; // null = nombre libre, no puede deber
        monto: number;        // editable
        accion: 'pago' | 'debe' | 'nada'; // 'nada' = no participa del cobro
    };
    const [cobroModal, setCobroModal] = useState<null | { jugadores: JugadorCobro[] }>(null);
    const [cobrando, setCobrando] = useState(false);

    // Construye la lista de jugadores para el sub-modal a partir del estado actual del turno.
    const armarJugadoresCobro = (): JugadorCobro[] => {
        const cancha = canchas.find(c => c.id === newTurno.canchaId);
        const titularSocio = socios.find(s => String(s.id) === String(newTurno.socioTitularId));
        const jugadores: JugadorCobro[] = [];
        if (newTurno.socioTitularId) {
            jugadores.push({
                key: 'titular',
                nombre: titularSocio ? `${titularSocio.nombre} ${titularSocio.apellido || ''}`.trim() : '(titular)',
                socio_id: newTurno.socioTitularId,
                monto: 0,
                accion: 'pago',
            });
        }
        newTurno.participantes.forEach((p, idx) => {
            const sid = p.socio_id;
            const isSocio = sid && sid !== 0 && String(sid) !== '0';
            const nombreLibre = (p.nombre_libre || '').trim();
            if (!isSocio && !nombreLibre) return; // ignorar slots vacíos
            const socio = isSocio ? socios.find(s => String(s.id) === String(sid)) : null;
            jugadores.push({
                key: `p-${idx}`,
                nombre: socio ? `${socio.nombre} ${socio.apellido || ''}`.trim() : (nombreLibre || `Jugador ${idx + 2}`),
                socio_id: isSocio ? (sid as string | number) : null,
                monto: 0,
                accion: 'pago',
            });
        });
        // Default: dividir parejo el precio entre quienes participan
        const n = Math.max(1, jugadores.length);
        const por = Math.round((newTurno.precio / n) * 100) / 100;
        jugadores.forEach(j => j.monto = por);
        // Ajustar redondeo en el último para que sume exacto
        const suma = jugadores.reduce((s, j) => s + j.monto, 0);
        const diff = Math.round((newTurno.precio - suma) * 100) / 100;
        if (jugadores.length > 0 && diff !== 0) {
            jugadores[jugadores.length - 1].monto = Math.round((jugadores[jugadores.length - 1].monto + diff) * 100) / 100;
        }
        return jugadores;
    };

    // Construye los participantesValidos (socios reales + nombres libres no vacíos) a partir del estado actual.
    const calcularParticipantesValidos = (): ParticipanteInput[] => {
        return newTurno.participantes.filter(p => {
            const sid = p.socio_id;
            const hasSocio = sid && sid !== 0 && String(sid) !== '0';
            const hasName = (p.nombre_libre || '').trim().length > 0;
            return hasSocio || hasName;
        });
    };

    const handleSaveTurno = async () => {
        if(!newTurno.canchaId || !newTurno.socioTitularId) {
            ui.showModal('Error', 'Falta cancha o titular');
            return;
        }

        const participantesValidos = calcularParticipantesValidos();

        // Si pasa a "pagado" y no estaba pagado → sub-paso "Cobro por jugador".
        if (newTurno.estado === 'pagado') {
            const wasPaid = editingTurnoId
                ? turnos.find(t => t.id === editingTurnoId)?.estado === 'pagado'
                : false;
            if (!wasPaid) {
                if (!sesionCaja || sesionCaja.estado !== 'abierta') {
                    ui.showToast('info', "Para marcar el turno como 'Pagado', debés tener la CAJA ABIERTA.");
                    return;
                }
                setCobroModal({ jugadores: armarJugadoresCobro() });
                return; // El guardado real ocurre cuando se confirma el cobro
            }
        }

        // No requiere cobro → guardar directo
        await guardarTurnoEnDB(participantesValidos);
    };

    // Procesa el cobro elegido por jugador: primero guarda el turno (para obtener id),
    // después registra la venta vinculada al turno y crea las deudas también vinculadas.
    const confirmCobroJugadores = async () => {
        if (!cobroModal) return;
        // Validación: nombres libres no pueden quedar como "debe" (no hay a quién asociarles la deuda).
        const sinSocio = cobroModal.jugadores.filter(j => j.accion === 'debe' && !j.socio_id);
        if (sinSocio.length > 0) {
            ui.showModal('Error', `No se puede dejar deuda a un jugador que no es socio: ${sinSocio.map(j => j.nombre).join(', ')}. Convertilo a socio primero (botón "+" en el modal del turno).`);
            return;
        }
        setCobrando(true);
        try {
            // 1) Guardar el turno primero para obtener su id.
            const participantesValidos = calcularParticipantesValidos();
            const turnoId = await guardarTurnoEnDB(participantesValidos);
            if (turnoId === null) {
                // guardarTurnoEnDB ya mostró el error; abortamos sin crear venta/deudas para no dejar huérfanos.
                return;
            }

            const totalPagado = cobroModal.jugadores
                .filter(j => j.accion === 'pago')
                .reduce((s, j) => s + (Number(j.monto) || 0), 0);

            // 2) Registrar venta por el total cobrado (si > 0), vinculada al turno.
            if (totalPagado > 0) {
                let productoCancha = productos.find(p => p.nombre === 'Alquiler de Cancha');
                if (!productoCancha) {
                    productoCancha = (await addProducto({
                        nombre: 'Alquiler de Cancha',
                        categoria: 'Servicios',
                        precio: 0,
                        estado: 'activo',
                        canjeable_por_puntos: false
                    })) || undefined;
                }
                if (!productoCancha) {
                    ui.showModal('Error interno', 'No se pudo asignar un producto para el cobro.');
                    return;
                }

                // Puntos: distribuir entre los socios que efectivamente pagaron.
                const sociosQuePagaron = cobroModal.jugadores
                    .filter(j => j.accion === 'pago' && j.socio_id)
                    .map(j => String(j.socio_id));
                const categoriaProducto = (productoCancha.categoria || '').toLowerCase();
                const reglaCategoria = reglasPuntos.find(r => (r.categoria || '').toLowerCase() === categoriaProducto);
                const reglaGeneral = reglasPuntos.find(r => !r.categoria);
                const reglaPuntos = reglaCategoria || reglaGeneral;
                const cantidadJugadores = Math.max(1, sociosQuePagaron.length);
                const montoPorJugador = totalPagado / cantidadJugadores;
                const puntosGanados = reglaPuntos && reglaPuntos.cada > 0
                    ? Math.floor(montoPorJugador / reglaPuntos.cada) * reglaPuntos.puntos
                    : 0;

                const itemVenta = {
                    producto: productoCancha,
                    producto_id: productoCancha.id,
                    cantidad: 1,
                    precio_unitario: totalPagado
                };
                const cobroExitoso = await registrarVenta({
                    // socio_id como string (UUID). El bug previo usaba Number(...) que daba NaN y la venta quedaba sin socio.
                    socio_id: newTurno.socioTitularId as any,
                    turno_id: turnoId as any,
                    items: [itemVenta],
                    total: totalPagado,
                    medio_de_pago: newTurno.medioPago,
                    puntos_ganados: puntosGanados,
                    puntos_canjeados: 0,
                    socios_puntos_ids: sociosQuePagaron
                } as any);
                if (!cobroExitoso) {
                    // Venta falló — revertimos el turno a "reservado" para que el usuario pueda reintentar el cobro.
                    try { await supabase.from('turnos').update({ estado: 'reservado' }).eq('id', turnoId); } catch (e) { console.warn('rollback turno → reservado falló', e); }
                    return;
                }
            }

            // 3) Crear deudas para los "debe" (solo socios — los nombres libres ya fueron bloqueados arriba).
            const deudasACrear: DeudaInput[] = cobroModal.jugadores
                .filter(j => j.accion === 'debe' && j.socio_id && j.monto > 0)
                .map(j => {
                    const cancha = canchas.find(c => c.id === newTurno.canchaId);
                    const desc = `${cancha?.nombre || 'Turno'} — ${selectedDate} ${newTurno.horaInicio}`;
                    return {
                        socio_id: j.socio_id as string | number,
                        monto: j.monto,
                        descripcion: desc,
                        turno_id: turnoId,
                    };
                });
            if (deudasACrear.length > 0 && addDeudas) {
                const res = await addDeudas(deudasACrear);
                if (!res.success) {
                    ui.showModal('Error', `Cobro registrado pero falló registrar las deudas: ${res.message || ''}. Revisalo en la pantalla Deudas.`);
                }
            }

            setCobroModal(null);
        } finally {
            setCobrando(false);
        }
    };

    // Persiste el turno en DB (crear / actualizar / recurrencia) sin ocuparse del cobro.
    // Retorna el id del turno persistido (para vincular venta/deudas) o null en error.
    const guardarTurnoEnDB = async (participantesValidos: ParticipanteInput[]): Promise<number | null> => {
        // Construir fechas ISO usando la duración personalizada del turno
        const fechaInicio = new Date(`${selectedDate}T${newTurno.horaInicio}:00`);
        const fechaFin = new Date(fechaInicio.getTime() + newTurno.duracionMinutos * 60000);
        let resultId: number | null = null;

        if (editingTurnoId) {
            // ACTUALIZAR EXISTENTE
            const turnoPayload = {
                cancha_id: newTurno.canchaId,
                fecha_inicio: fechaInicio.toISOString(),
                fecha_fin: fechaFin.toISOString(),
                precio: newTurno.precio,
                estado: newTurno.estado,
                socio_titular_id: newTurno.socioTitularId as number,
                notas: newTurno.notas
            };
            if (editingTurnoId < 0) {
                // Synthetic occurrence: crear turno real y excepción para evitar duplicados
                const recId = Math.floor((-editingTurnoId) / 10000);
                const fecha = fechaInicio.toISOString().split('T')[0];

                try {
                    await supabase.from('turnos_recurrentes_excepciones').insert([
                        { recurrencia_id: recId, fecha, creado_por: user?.nombre, club_id: user?.club_id }
                    ]);
                } catch (e) {
                    console.warn('No se pudo crear excepción de recurrencia:', e);
                }

                const created = await addTurno({
                    cancha_id: newTurno.canchaId,
                    fecha_inicio: fechaInicio.toISOString(),
                    fecha_fin: fechaFin.toISOString(),
                    precio: newTurno.precio,
                    estado: newTurno.estado,
                    socio_titular_id: newTurno.socioTitularId as number,
                    notas: newTurno.notas
                }, participantesValidos);

                if (!created) {
                    ui.showModal('Error', 'No se pudo guardar el turno.');
                    return null;
                }
                resultId = created.id;

                if (user?.club_id) {
                    await fetchClubData(user.club_id);
                }
            } else {
                const updated = await updateTurno(editingTurnoId, turnoPayload, participantesValidos);
                if (!updated) {
                    ui.showModal('Error', 'No se pudieron guardar los cambios del turno.');
                    return null;
                }
                resultId = editingTurnoId;
            }
        } else {
            // CREAR NUEVO (posible recurrencia semanal si es fijo)
            if (!newTurno.isFijo) {
                const created = await addTurno({
                    cancha_id: newTurno.canchaId,
                    fecha_inicio: fechaInicio.toISOString(),
                    fecha_fin: fechaFin.toISOString(),
                    precio: newTurno.precio,
                    estado: newTurno.estado,
                    socio_titular_id: newTurno.socioTitularId as number,
                    notas: newTurno.notas
                }, participantesValidos);

                if (!created) {
                    ui.showModal('Error', 'No se pudo guardar el turno.');
                    return null;
                }
                resultId = created.id;
            } else {
                // Crear una recurrencia en DB y solo un turno inicial
                if (!newTurno.socioTitularId) {
                    ui.showModal('Error', 'Seleccioná un titular para crear un turno fijo.');
                    setIsTurnoModalOpen(true);
                    return;
                }

                const recData = {
                    cancha_id: newTurno.canchaId,
                    socio_titular_id: String(newTurno.socioTitularId),
                    dia_semana: fechaInicio.getDay(),
                    hora_inicio: newTurno.horaInicio,
                    duracion_minutos: newTurno.duracionMinutos,
                    precio: newTurno.precio,
                    estado: newTurno.estado,
                    notas: newTurno.notas,
                    start_date: fechaInicio.toISOString().split('T')[0],
                    activo: true
                };

                if (typeof addTurnoRecurrencia === 'function') {
                    await addTurnoRecurrencia(recData, participantesValidos);
                    // Recurrencia no expone el id del primer turno creado; el vínculo de venta/deuda queda sin turno_id en este caso.
                } else {
                    // Fallback: si la API no existe, crear solo el turno actual
                    const created = await addTurno({
                        cancha_id: newTurno.canchaId,
                        fecha_inicio: fechaInicio.toISOString(),
                        fecha_fin: fechaFin.toISOString(),
                        precio: newTurno.precio,
                        estado: newTurno.estado,
                        socio_titular_id: newTurno.socioTitularId as number,
                        notas: newTurno.notas
                    }, participantesValidos);

                    if (!created) {
                        ui.showModal('Error', 'No se pudo guardar el turno.');
                        return null;
                    }
                    resultId = created.id;
                }
            }
        }

        setIsTurnoModalOpen(false);
        return resultId;
    };

    const handleClickEliminar = () => {
        if (!editingTurnoId) return;
        const turno = turnos.find(t => t.id === editingTurnoId);
        if (turno) {
            setTurnoToDelete(turno);
            setIsTurnoModalOpen(false); // Cerramos el modal de edición
            setIsDeleteChoiceOpen(true);
        }
    };

    const confirmDelete = async () => {
        if (!turnoToDelete) return;
        setIsDeleting(true);
        try {
            const success = await deleteTurno(turnoToDelete.id);
            if (!success) {
                ui.showModal('Error', 'Hubo un error al eliminar el turno. Intentá de nuevo.');
            }
        } catch (e) {
            console.error(e);
            ui.showModal('Error', 'Error inesperado.');
        } finally {
            setIsDeleting(false);
            setTurnoToDelete(null); // Cerramos el modal de confirmación
            setIsDeleteChoiceOpen(false);
        }
    };

    const handleDeleteOnlyThis = async () => {
        if (!turnoToDelete) return;
        setIsDeleting(true);
        try {
            // Synthetic occurrence: create an exception row for the recurrence date
            if (typeof turnoToDelete.id === 'number' && turnoToDelete.id < 0) {
                const recId = Math.floor((-turnoToDelete.id) / 10000);
                const fecha = String(turnoToDelete.fecha_inicio).split('T')[0];
                const { error } = await supabase.from('turnos_recurrentes_excepciones').insert([{ recurrencia_id: recId, fecha, creado_por: user?.nombre, club_id: user?.club_id }]);
                if (error) throw error;
                // Refresh local data
                if (user?.club_id) await fetchClubData(user.club_id);
                try { ui.showToast('success', 'Se eliminó esta aparición (excepción creada).'); } catch(e){}
                return;
            }

            // Non-synthetic: check if it belongs to a recurrence; if so, create exception and delete the specific DB turno
            const start = new Date(turnoToDelete.fecha_inicio);
            const hh = String(start.getHours()).padStart(2,'0');
            const mm = String(start.getMinutes()).padStart(2,'0');
            const hhmmss = `${hh}:${mm}:00`;
            const fechaOnly = start.toISOString().split('T')[0];

            const { data: recs } = await supabase.from('turnos_recurrentes')
                .select('id')
                .eq('cancha_id', turnoToDelete.cancha_id)
                .eq('socio_titular_id', turnoToDelete.socio_titular_id)
                .eq('hora_inicio', hhmmss)
                .lte('start_date', fechaOnly)
                .limit(1);

            if (recs && recs.length > 0) {
                const recId = recs[0].id;
                const { error } = await supabase.from('turnos_recurrentes_excepciones').insert([{ recurrencia_id: recId, fecha: fechaOnly, creado_por: user?.nombre, club_id: user?.club_id }]);
                if (error) throw error;
                // delete the specific DB turno if exists
                const { error: delErr } = await supabase.from('turnos').delete().eq('id', turnoToDelete.id);
                if (delErr) throw delErr;
                if (user?.club_id) await fetchClubData(user.club_id);
                try { ui.showToast('success', 'Se eliminó esta aparición (excepción creada).'); } catch(e){}
            } else {
                // Not part of a recurrence: delete normally
                const success = await deleteTurno(turnoToDelete.id);
                if (!success) ui.showModal('Error', 'No se pudo eliminar el turno.');
            }
        } catch (err) {
            console.error(err);
            ui.showModal('Error', 'Error inesperado al eliminar.');
        } finally {
            setIsDeleting(false);
            setTurnoToDelete(null);
            setIsDeleteChoiceOpen(false);
        }
    };

    const handleDeleteThisAndFuture = async () => {
        if (!turnoToDelete) return;
        setIsDeleting(true);
        try {
            // If synthetic id, extract recId
            if (typeof turnoToDelete.id === 'number' && turnoToDelete.id < 0) {
                const recId = Math.floor((-turnoToDelete.id) / 10000);
                const fechaOnly = String(turnoToDelete.fecha_inicio).split('T')[0];
                // Set end_date to day before the selected occurrence so past occurrences remain
                const dateBefore = new Date(fechaOnly);
                dateBefore.setDate(dateBefore.getDate() - 1);
                const endDateStr = dateBefore.toISOString().split('T')[0];
                const { error: updErr } = await supabase.from('turnos_recurrentes').update({ end_date: endDateStr }).eq('id', recId);
                if (updErr) throw updErr;
                // Remove any concrete DB turnos from this date onwards that belong to this recurrence
                const { error: delErr } = await supabase.from('turnos').delete()
                    .gte('fecha_inicio', fechaOnly + 'T00:00:00')
                    .eq('cancha_id', turnoToDelete.cancha_id)
                    .eq('socio_titular_id', turnoToDelete.socio_titular_id)
                    .like('notas', '%[FIJO]%');
                if (delErr) console.warn('No se pudieron eliminar algunos turnos concretos posteriores a la fecha:', delErr.message || delErr);
            } else {
                // find recurrence record matching this turno
                const start = new Date(turnoToDelete.fecha_inicio);
                const hh = String(start.getHours()).padStart(2, '0');
                const mm = String(start.getMinutes()).padStart(2, '0');
                const hhmmss = `${hh}:${mm}:00`;
                const { data: recs } = await supabase.from('turnos_recurrentes')
                    .select('id')
                    .eq('cancha_id', turnoToDelete.cancha_id)
                    .eq('socio_titular_id', turnoToDelete.socio_titular_id)
                    .eq('hora_inicio', hhmmss)
                    .lte('start_date', start.toISOString().split('T')[0])
                    .limit(1);

                if (recs && recs.length > 0) {
                    const recId = recs[0].id;
                    const fechaOnlyStr = start.toISOString().split('T')[0];
                    const dateBefore = new Date(fechaOnlyStr);
                    dateBefore.setDate(dateBefore.getDate() - 1);
                    const endDateStr = dateBefore.toISOString().split('T')[0];
                    const { error: updErr } = await supabase.from('turnos_recurrentes').update({ end_date: endDateStr }).eq('id', recId);
                    if (updErr) throw updErr;
                    // delete concrete DB turnos if any from this date onwards
                    const { error: delErr } = await supabase.from('turnos').delete()
                        .gte('fecha_inicio', fechaOnlyStr + 'T00:00:00')
                        .eq('cancha_id', turnoToDelete.cancha_id)
                        .eq('socio_titular_id', turnoToDelete.socio_titular_id)
                        .like('notas', '%[FIJO]%');
                    if (delErr) console.warn('No se pudieron eliminar algunos turnos concretos posteriores a la fecha:', delErr.message || delErr);
                } else {
                    // fallback: delete DB turnos with [FIJO] notes (existing behavior)
                    const success = await deleteTurno(turnoToDelete.id);
                    if (!success) ui.showModal('Error', 'No se pudo eliminar la recurrencia.');
                }
            }
        } catch (err: any) {
            console.error(err);
            ui.showModal('Error', 'Error eliminando la recurrencia: ' + (err?.message || err));
        } finally {
            setIsDeleting(false);
            setTurnoToDelete(null);
            setIsDeleteChoiceOpen(false);
        }
    };

    // --- CREACION RAPIDA DE SOCIO ---
    const handleQuickCreateSocio = async () => {
        if(!inlineSocioName || !inlineSocioPhone || !inlineSocioDni || !inlineSocioEmail) {
            ui.showModal('Error', 'Nombre, teléfono, DNI y email son requeridos');
            return;
        }
        
        const nuevoSocio = await addSocio({
            nombre: inlineSocioName,
            apellido: '',
            telefono: inlineSocioPhone,
            documento: inlineSocioDni, 
            email: inlineSocioEmail,
            notas: 'Creado desde Turnos', 
            estado: 'activo'
        });

        if(nuevoSocio) {
            const newId = nuevoSocio.id; 
            
            if(inlineSocioTarget === 'titular') {
                setNewTurno(prev => ({ ...prev, socioTitularId: newId }));
            } else {
                setNewTurno(prev => {
                    const nuevosParticipantes = [...prev.participantes];
                    if (typeof inlineSocioTarget === 'number') {
                        nuevosParticipantes[inlineSocioTarget] = { socio_id: newId };
                    }
                    return { ...prev, participantes: nuevosParticipantes };
                });
            }
            setIsCreatingSocioInline(false);
            setInlineSocioName('');
            setInlineSocioPhone('');
            setInlineSocioDni('');
            setInlineSocioEmail('');
        }
    };

    return (
        <div className="flex flex-col h-full bg-gray-50 p-4">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">Gestión de Turnos</h2>
                    <p className="text-sm text-gray-500">Reservas y disponibilidad de canchas</p>
                </div>
                <div className="flex gap-4">
                    <input 
                        type="date" 
                        value={selectedDate} 
                        onChange={e => setSelectedDate(e.target.value)}
                        className="p-2 border rounded-md"
                    />
                    <button 
                        onClick={() => { setNewCancha({ nombre: '', deporte: 'Padel', precio_base: 0, duracion_minutos: 60, estado: 'activa' }); setEditingCanchaId(null); setIsCanchaModalOpen(true); }}
                        disabled={isReadOnly}
                        className="bg-gray-800 text-white px-4 py-2 rounded-md text-sm hover:bg-gray-700 disabled:bg-gray-400"
                    >
                        + Nueva Cancha
                    </button>
                </div>
            </div>

            {/* GRILLA DE TURNOS */}
            <div className="flex-1 overflow-auto bg-white rounded-lg shadow border">
                    <div className="min-w-[800px] relative" ref={gridRef}>
                    {/* Header Canchas */}
                        <div className="flex border-b sticky top-0 bg-gray-100 z-10 canchas-header">
                        <div className="w-16 flex-shrink-0 p-3 border-r bg-gray-200 text-gray-900 font-bold text-center text-xs">Hora</div>
                        {canchas.map(c => (
                            <div key={c.id} className="flex-1 p-3 border-r font-semibold text-center text-gray-700 min-w-[150px]">
                                <div className="flex items-center justify-center gap-2">
                                    <span>{c.nombre} <span className="text-xs font-normal text-gray-500">({c.deporte})</span></span>
                                    {user?.rol === Rol.ADMIN && (
                                        <button
                                            onClick={() => { setNewCancha(c); setEditingCanchaId(c.id); setIsCanchaModalOpen(true); }}
                                            disabled={isReadOnly}
                                            className="text-xs bg-white px-2 py-1 rounded border hover:bg-gray-50"
                                        >
                                            Editar
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                         {canchas.length === 0 && <div className="p-4 text-gray-500 italic">No hay canchas configuradas. Creá una para empezar.</div>}
                    </div>

                    {/* Body Grilla */}
                    {/* Body Grilla: filas horarias */}
                    {hours.map(hour => (
                        <div key={hour} className="flex border-b hour-row min-h-[90px]">
                            <div className="w-16 flex-shrink-0 p-2 border-r bg-gray-50 text-gray-900 text-xs font-bold text-center flex items-center justify-center hour-col">
                                {hour}:00
                            </div>
                            {canchas.map(cancha => {
                                const occupied = !!getTurnoEnCelda(cancha.id, hour);

                                // Determine if this cell intersects the clickable bottom gap of any overlay
                                // We compute this even when `occupied` is true so the gap remains clickable.
                                let showGapReserved = false;
                                if (measuredRowHeight > 0 && gridRef.current) {
                                    const baseHour = hours[0];
                                    const grid = gridRef.current as HTMLElement;
                                    const firstRowEl = grid.querySelector('.hour-row') as HTMLElement | null;
                                    const gridRect = grid.getBoundingClientRect();
                                    const firstRowOffset = firstRowEl ? (firstRowEl.getBoundingClientRect().top - gridRect.top - headerHeight) : 0;
                                    const pxPerMinute = measuredRowHeight / 60;

                                    for (const turno of turnosDelDia) {
                                        if (turno.cancha_id !== cancha.id) continue;
                                        const start = parseISOToLocalDate(turno.fecha_inicio);
                                        const end = parseISOToLocalDate(turno.fecha_fin);
                                        const minutesFromBase = Math.round((start.getTime() - new Date(`${selectedDate}T${String(baseHour).padStart(2,'0')}:00:00`).getTime()) / 60000);
                                        const durationMinutes = Math.round((end.getTime() - start.getTime()) / 60000);
                                        const top = firstRowOffset + minutesFromBase * pxPerMinute + 10; // include visualOffset
                                        const height = Math.max(20, durationMinutes * pxPerMinute - (10 + 12));
                                        const gapTop = top + height;
                                        const gapBottom = gapTop + 12; // bottomGap

                                        const cellTop = firstRowOffset + (hour - baseHour) * measuredRowHeight;
                                        const cellBottom = cellTop + measuredRowHeight;

                                        if (gapTop < cellBottom && gapBottom > cellTop) {
                                            // gap time corresponds to the end of this turno
                                            const gapTime = end.getTime();
                                            // If there is any turno that starts exactly at the gap time on this cancha,
                                            // then the gap should NOT be considered clickable (prevents adjacent blocks showing a gap)
                                            const startsAtGap = turnosDelDia.some(t2 => t2.cancha_id === cancha.id && parseISOToLocalDate(t2.fecha_inicio).getTime() === gapTime);
                                            if (!startsAtGap) {
                                                showGapReserved = true;
                                                break;
                                            }
                                        }
                                    }
                                }

                                // If a cell is occupied but has a clickable gap, we should still render the reserve button
                                const cellHasClickableGap = showGapReserved;

                                // Determine hover selection halves
                                const hoverIsTop = hoverReserve.canchaId === cancha.id && hoverReserve.hour === hour && hoverReserve.label?.endsWith(':00');
                                const hoverIsBottom = hoverReserve.canchaId === cancha.id && hoverReserve.hour === hour && hoverReserve.label?.endsWith(':30');

                                return (
                                    <div key={cancha.id} className="flex-1 border-r relative group min-w-[150px]">
                                        {(!occupied || cellHasClickableGap) ? (
                                            // Render the interactive halves even when occupied if there's a clickable gap
                                            <button
                                                onClick={(e) => handleOpenTurno(cancha, hour, e)}
                                                onMouseMove={(e) => {
                                                    // Compute hover preview time using actual cell height
                                                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                                    const clickY = e.clientY - rect.top;
                                                    const rawMinutes = Math.floor((clickY / Math.max(1, rect.height)) * 60);
                                                    const minute = rawMinutes < 30 ? 0 : 30;
                                                    setHoverReserve({ canchaId: cancha.id, hour, label: `${String(hour).padStart(2,'0')}:${String(minute).padStart(2,'0')}` });
                                                }}
                                                onMouseLeave={() => setHoverReserve({ canchaId: null, hour: null, label: null })}
                                                className={`w-full h-full relative p-0 border-0 bg-transparent text-xs`}
                                                disabled={isReadOnly}
                                            >
                                                <div className="absolute inset-0">
                                                    {/* Top half */}
                                                    <div className={`absolute left-0 right-0 top-0 h-1/2 transition-colors ${hoverIsTop ? 'bg-blue-600' : 'bg-transparent group-hover:bg-gray-100'}`} />
                                                    {/* Bottom half */}
                                                    <div className={`absolute left-0 right-0 bottom-0 h-1/2 transition-colors ${hoverIsBottom ? 'bg-blue-600' : 'bg-transparent group-hover:bg-gray-100'}`} />
                                                    {/* Texts centered in each half */}
                                                    <div className="absolute left-0 right-0 top-0 h-1/2 flex items-center justify-center">
                                                        <span className={`${hoverIsTop ? 'text-white' : 'text-transparent group-hover:text-gray-400'}`}>{hoverIsTop ? `+ Reservar ${hoverReserve.label}` : '+ Reservar'}</span>
                                                    </div>
                                                    <div className="absolute left-0 right-0 bottom-0 h-1/2 flex items-center justify-center">
                                                        <span className={`${hoverIsBottom ? 'text-white' : 'text-transparent group-hover:text-gray-400'}`}>{hoverIsBottom ? `+ Reservar ${hoverReserve.label}` : '+ Reservar'}</span>
                                                    </div>
                                                </div>
                                            </button>
                                        ) : (
                                            null
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    ))}

                    {/* Overlay absoluto para mostrar turnos como bloques que ocupan varias filas */}
                    {measurementReady && measuredRowHeight > 0 && gridRef.current && (
                        <div style={{ position: 'absolute', top: headerHeight, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
                                            {turnosDelDia.map(turno => {
                                                        const start = parseISOToLocalDate(turno.fecha_inicio);
                                                        const end = parseISOToLocalDate(turno.fecha_fin);
                                                        const baseHour = hours[0];
                                                        const baseDate = new Date(`${selectedDate}T${String(baseHour).padStart(2,'0')}:00:00`);

                                                        // Use the exact measured row height for precise minute->pixel mapping
                                                        const pxPerMinute = measuredRowHeight / 60;

                                                        // Minutes offset from baseDate
                                                        const minutesFromBase = Math.round((start.getTime() - baseDate.getTime()) / 60000);
                                                        const durationMinutes = Math.round((end.getTime() - start.getTime()) / 60000);

                                                        // Find offset of the first row relative to the grid top and header
                                                        const grid = gridRef.current as HTMLElement;
                                                        const firstRowEl = grid ? grid.querySelector('.hour-row') as HTMLElement | null : null;
                                                        const gridRect = grid ? grid.getBoundingClientRect() : { top: 0 } as DOMRect;
                                                        const firstRowOffset = firstRowEl ? (firstRowEl.getBoundingClientRect().top - gridRect.top - headerHeight) : 0;

                                                        // Compute top and height in pixels precisely (minutes -> pixels) relative to overlay top
                                                        let top = firstRowOffset + minutesFromBase * pxPerMinute;
                                                        let height = durationMinutes * pxPerMinute;

                                                        // Clamp to grid body bounds (firstRowOffset .. firstRowOffset + bodyHeight)
                                                        const bodyHeight = hours.length * measuredRowHeight;
                                                        const maxTop = firstRowOffset + bodyHeight;
                                                        if (top < firstRowOffset) top = firstRowOffset;
                                                        if (top > maxTop) top = maxTop;
                                                        if (height < 20) height = Math.max(20, height);
                                                        if (top + height > maxTop) height = Math.max(20, maxTop - top);

                                                        const colIndex = canchas.findIndex(c => c.id === turno.cancha_id);
                                                        if (colIndex === -1) return null;
                                                        const left = leftColWidth + colIndex * columnWidth + 8; // small padding
                                                        const width = Math.max(140, columnWidth - 16);

                                                        const startH = start.getHours();
                                                        const startM = start.getMinutes();
                                                        const endH = end.getHours();
                                                        const endM = end.getMinutes();

                                                        // Apply a small persistent visual nudge down, but leave a clickable gap below the block
                                                        const verticalOffset = 10; // visual nudge down
                                                        const bottomGap = 6; // clickable gap below block (reduced to tighten spacing)
                                                        const visualTop = top + verticalOffset;
                                                        // Ensure overlays have a reasonable minimum height so content (status text) doesn't get clipped
                                                        const adjustedHeight = Math.max(56, height - (verticalOffset + bottomGap));

                                                        return (
                                                            <div
                                                                key={turno.id}
                                                                onClick={(e) => { e.stopPropagation(); handleEditTurno(turno); }}
                                                                style={{ position: 'absolute', top: visualTop, left, width, height: adjustedHeight, zIndex: 30, pointerEvents: 'auto' }}
                                                                className={`rounded p-2 text-xs overflow-hidden cursor-pointer hover:shadow-md transition-shadow ${turno.notas && String(turno.notas).includes('[FIJO]') ? 'bg-yellow-100 border-yellow-300 text-yellow-800' : (turno.estado === 'pagado' ? 'bg-green-100 border-green-300 text-green-800' : turno.estado === 'cancelado' ? 'bg-gray-200 border-gray-400 text-gray-600 opacity-90' : 'bg-blue-100 border-blue-300 text-blue-800')} border`}
                                                            >
                                                                    <div className="font-semibold truncate">{turno.socio_titular?.nombre} {turno.socio_titular?.apellido}</div>
                                                                    <div className="truncate text-xs">{turno.cancha?.deporte} • {String(startH).padStart(2,'0')}:{String(startM).padStart(2,'0')} - {String(endH).padStart(2,'0')}:{String(endM).padStart(2,'0')}</div>
                                                                    {turno.estado === 'reservado' && <div className="text-xs font-bold mt-1 text-red-500">PENDIENTE PAGO</div>}
                                                            </div>
                                                        );
                                                    })}
                        </div>
                    )}
                </div>
            </div>

            {/* MODAL NUEVA CANCHA */}
            <Modal isOpen={isCanchaModalOpen} onClose={() => { setIsCanchaModalOpen(false); setNewCancha({ nombre: '', deporte: 'Padel', precio_base: 0, duracion_minutos: 60, estado: 'activa' }); setEditingCanchaId(null); }} title="Agregar Cancha / Espacio">
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Nombre (ej. Cancha 1)</label>
                        <input type="text" className="w-full p-2 border rounded" value={newCancha.nombre} onChange={e => setNewCancha({...newCancha, nombre: e.target.value})} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Deporte</label>
                            <select className="w-full p-2 border rounded" value={newCancha.deporte} onChange={e => setNewCancha({...newCancha, deporte: e.target.value as Deporte})}>
                                <option value="Padel">Padel</option>
                                <option value="Futbol">Futbol</option>
                                <option value="Tenis">Tenis</option>
                                <option value="Hockey">Hockey</option>
                                <option value="Otro">Otro</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Precio Base</label>
                            <input type="number" className="w-full p-2 border rounded" value={newCancha.precio_base} onChange={e => setNewCancha({...newCancha, precio_base: parseFloat(e.target.value)})} />
                        </div>
                    </div>
                    <button onClick={handleSaveCancha} className="w-full bg-blue-600 text-white p-2 rounded hover:bg-blue-700">Guardar Cancha</button>
                </div>
            </Modal>

            {/* MODAL NUEVO/EDITAR TURNO */}
            <Modal isOpen={isTurnoModalOpen} onClose={() => { setIsTurnoModalOpen(false); setIsCreatingSocioInline(false); }} title={editingTurnoId ? "Editar Turno" : "Reservar Turno"}>
                {isCreatingSocioInline ? (
                    <div className="space-y-4 bg-gray-50 p-4 rounded border">
                        <h4 className="font-bold text-sm">Nuevo Socio Rápido</h4>
                        <input type="text" placeholder="Nombre" className="w-full p-2 border rounded" value={inlineSocioName} onChange={e => setInlineSocioName(e.target.value)} />
                        <input type="text" placeholder="Teléfono (Obligatorio)" className="w-full p-2 border rounded" value={inlineSocioPhone} onChange={e => setInlineSocioPhone(e.target.value)} />
                        <input type="text" placeholder="DNI (Obligatorio)" className="w-full p-2 border rounded" value={inlineSocioDni} onChange={e => setInlineSocioDni(e.target.value)} />
                        <input type="email" placeholder="Email (Obligatorio)" className="w-full p-2 border rounded" value={inlineSocioEmail} onChange={e => setInlineSocioEmail(e.target.value)} />
                        <div className="flex gap-2">
                            <button onClick={handleQuickCreateSocio} className="bg-green-600 text-white px-3 py-1 rounded text-sm">Crear y Seleccionar</button>
                            <button onClick={() => setIsCreatingSocioInline(false)} className="text-gray-500 text-sm">Cancelar</button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="bg-blue-50 text-blue-900 p-3 rounded text-sm mb-4 flex justify-between items-center">
                             <span><span className="font-bold">{canchas.find(c => c.id === newTurno.canchaId)?.nombre}</span> - {newTurno.horaInicio} hs</span>
                             {editingTurnoId && <span className="text-xs bg-white px-2 py-1 rounded border">ID: {editingTurnoId}</span>}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Titular */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Titular de la Reserva</label>
                                <div className="flex gap-2">
                                    <select 
                                        id="titular-select"
                                        className="w-full p-2 border rounded" 
                                        value={newTurno.socioTitularId !== null ? String(newTurno.socioTitularId) : ""} 
                                        onChange={e => {
                                            const valStr = e.target.value;
                                            const val = valStr === "" ? null : valStr;
                                            setNewTurno(prev => ({...prev, socioTitularId: val}));
                                        }}
                                    >
                                        <option value="">Seleccionar Socio...</option>
                                        {getSociosDisponibles(newTurno.socioTitularId).map(s => (
                                            <option key={s.id} value={String(s.id)}>
                                                {s.nombre} {s.apellido}
                                            </option>
                                        ))}
                                    </select>
                                    <button onClick={() => { setIsCreatingSocioInline(true); setInlineSocioTarget('titular'); }} className="bg-gray-200 px-3 rounded hover:bg-gray-300 font-bold">+</button>
                                </div>
                            </div>

                            {/* Duración */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Duración por Turno</label>
                                <div className="flex gap-2 items-center">
                                    <select 
                                        className="flex-1 p-2 border rounded"
                                        value={isDuracionCustomizada ? 'custom' : newTurno.duracionMinutos}
                                        onChange={e => {
                                            const value = e.target.value;
                                            if (value === 'custom') {
                                                setIsDuracionCustomizada(true);
                                                if (newTurno.duracionMinutos === 60 || newTurno.duracionMinutos === 90) {
                                                    setNewTurno(prev => ({...prev, duracionMinutos: 60}));
                                                }
                                            } else {
                                                setIsDuracionCustomizada(false);
                                                setNewTurno(prev => ({...prev, duracionMinutos: parseInt(value)}));
                                            }
                                        }}
                                    >
                                        <option value={60}>1 hora</option>
                                        <option value={90}>1.30 horas</option>
                                        <option value="custom">Personalizado</option>
                                    </select>
                                    {isDuracionCustomizada && (
                                        <input 
                                            type="number" 
                                            min="15"
                                            max="480"
                                            step="15"
                                            className="w-28 p-2 border rounded" 
                                            placeholder="Min."
                                            value={newTurno.duracionMinutos}
                                            onChange={e => setNewTurno(prev => ({...prev, duracionMinutos: Math.max(15, parseInt(e.target.value) || 60)}))}
                                        />
                                    )}
                                </div>
                                <p className="text-xs text-gray-500 mt-1">* Duración en minutos (ej: 60, 75, 120)</p>
                            </div>

                            {/* Jugadores adicionales — Padel */}
                            {canchas.find(c => c.id === newTurno.canchaId)?.deporte === 'Padel' && (
                                <div className="md:col-span-2 border-t pt-2 mt-2">
                                    <label className="block text-xs font-bold text-gray-500 mb-2 uppercase">Jugadores Adicionales (Opcional)</label>
                                    <div className="flex flex-wrap gap-2">
                                        {newTurno.participantes.map((p, idx) => {
                                            const pid = p.socio_id ?? 0;
                                            return (
                                                <div key={idx} className="flex-1 min-w-[190px] flex gap-2 mb-2">
                                                    <select
                                                        className="flex-1 p-2 border rounded text-sm"
                                                        value={String(pid || 0)}
                                                        onChange={e => {
                                                            const valStr = e.target.value;
                                                            const val: string | number = valStr === "0" ? 0 : valStr;
                                                            setNewTurno(prev => {
                                                                const newParts = [...prev.participantes];
                                                                newParts[idx] = { socio_id: val };
                                                                return { ...prev, participantes: newParts };
                                                            });
                                                        }}
                                                    >
                                                        <option value="0">Jugador {idx + 2} (Vacío)</option>
                                                        {getSociosDisponibles(pid).map(s => (
                                                            <option key={`p-${idx}-${s.id}`} value={String(s.id)}>
                                                                {s.nombre} {s.apellido}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <button onClick={() => { setIsCreatingSocioInline(true); setInlineSocioTarget(idx); }} className="bg-gray-200 px-3 rounded hover:bg-gray-300 text-sm font-bold">+</button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Jugadores — Fútbol (12 o 16, nombres libres) */}
                            {canchas.find(c => c.id === newTurno.canchaId)?.deporte === 'Futbol' && (
                                <div className="md:col-span-2 border-t pt-2 mt-2">
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-xs font-bold text-gray-500 uppercase">Jugadores</label>
                                        <div className="flex gap-2 items-center">
                                            <span className="text-xs text-gray-500">Cantidad:</span>
                                            {[12, 16].map(n => (
                                                <button
                                                    key={n}
                                                    type="button"
                                                    onClick={() => {
                                                        const target = n as 12 | 16;
                                                        setFutbolCantidad(target);
                                                        setNewTurno(prev => {
                                                            const desired = target - 1;
                                                            const next = [...prev.participantes];
                                                            if (next.length < desired) {
                                                                while (next.length < desired) next.push({ nombre_libre: '' });
                                                            } else if (next.length > desired) {
                                                                next.length = desired;
                                                            }
                                                            return { ...prev, participantes: next };
                                                        });
                                                    }}
                                                    className={`px-3 py-1 rounded text-sm border ${futbolCantidad === n ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                                                >
                                                    {n}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-500 mb-2">Escribí el nombre de cada jugador. Si coincide con un socio te lo sugiere para vincularlo (necesario si después le vas a dejar deuda). Si no, queda como nombre libre.</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 pr-1">
                                        {newTurno.participantes.map((p, idx) => (
                                            <JugadorFutbolRow
                                                key={idx}
                                                p={p}
                                                idx={idx}
                                                socios={socios}
                                                excludedIds={selectedIdsSet}
                                                onChange={(next) => {
                                                    setNewTurno(prev => {
                                                        const newParts = [...prev.participantes];
                                                        newParts[idx] = next;
                                                        return { ...prev, participantes: newParts };
                                                    });
                                                }}
                                                onCrearSocio={(preName) => {
                                                    setInlineSocioName(preName);
                                                    setInlineSocioPhone('');
                                                    setInlineSocioDni('');
                                                    setInlineSocioEmail('');
                                                    setIsCreatingSocioInline(true);
                                                    setInlineSocioTarget(idx);
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Precio */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Precio</label>
                                <input type="number" className="w-full p-2 border rounded" value={newTurno.precio} onChange={e => setNewTurno({...newTurno, precio: parseFloat(e.target.value)})} />
                            </div>

                            {/* Estado */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Estado</label>
                                <select 
                                    className="w-full p-2 border rounded"
                                    value={newTurno.estado}
                                    onChange={e => setNewTurno(prev => ({...prev, estado: e.target.value as Turno['estado']}))}
                                >
                                    <option value="reservado">Reservado</option>
                                    <option value="pagado">Pagado</option>
                                    <option value="jugando">Jugando</option>
                                    <option value="finalizado">Finalizado</option>
                                    <option value="cancelado">Cancelado</option>
                                </select>
                            </div>

                            {/* Medio de pago (full width cuando aparece) */}
                            {newTurno.estado === 'pagado' && (
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-green-700">Medio de Pago</label>
                                    <select 
                                        className="w-full p-2 border border-green-300 bg-green-50 text-gray-900 rounded"
                                        value={newTurno.medioPago}
                                        onChange={e => setNewTurno(prev => ({...prev, medioPago: e.target.value as MedioDePago}))}
                                    >
                                        <option value={MedioDePago.EFECTIVO} className="text-gray-900">Efectivo</option>
                                        <option value={MedioDePago.TRANSFERENCIA} className="text-gray-900">Transferencia Bancaria</option>
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1">* Se registrará automáticamente una venta en la caja.</p>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={!!newTurno.isFijo} onChange={e => setNewTurno(prev => ({...prev, isFijo: e.target.checked}))} />
                                <span className="text-sm">Turno fijo</span>
                            </label>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Notas</label>
                            <input type="text" className="w-full p-2 border rounded" placeholder="Ej. Pelotas alquiladas" value={newTurno.notas} onChange={e => setNewTurno({...newTurno, notas: e.target.value})} />
                        </div>

                        <div className="flex gap-2 mt-4 justify-between items-center">
                            <div>
                                {editingTurnoId && user?.rol === Rol.ADMIN && (
                                    <button 
                                        onClick={handleClickEliminar}
                                        className="bg-red-100 text-red-700 py-2 px-4 rounded font-bold hover:bg-red-200"
                                    >
                                        Eliminar
                                    </button>
                                )}
                            </div>
                            <div className="flex-1 md:flex-none md:w-64">
                                <button onClick={handleSaveTurno} className="w-full bg-blue-600 text-white py-2 rounded font-bold hover:bg-blue-700">
                                    {editingTurnoId ? "Guardar Cambios" : "Confirmar Reserva"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            {/* MODAL "COBRO POR JUGADOR" */}
            {cobroModal && (() => {
                const totalPago = cobroModal.jugadores.filter(j => j.accion === 'pago').reduce((s, j) => s + (Number(j.monto) || 0), 0);
                const totalDebe = cobroModal.jugadores.filter(j => j.accion === 'debe').reduce((s, j) => s + (Number(j.monto) || 0), 0);
                const totalNada = cobroModal.jugadores.filter(j => j.accion === 'nada').reduce((s, j) => s + (Number(j.monto) || 0), 0);
                const updateJugador = (key: string, patch: Partial<typeof cobroModal.jugadores[0]>) => {
                    setCobroModal(prev => prev ? { jugadores: prev.jugadores.map(j => j.key === key ? { ...j, ...patch } : j) } : prev);
                };
                return (
                    <Modal isOpen={true} onClose={() => !cobrando && setCobroModal(null)} title="Cobro por jugador">
                        <div className="space-y-4">
                            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900">
                                Indicá qué jugadores <b>pagaron</b> y cuáles quedaron <b>debiendo</b>. El total cobrado entra a caja; las deudas quedan asociadas al socio para cobrarlas más tarde desde "Deudas".
                            </div>
                            <div className="border rounded divide-y max-h-96 overflow-y-auto">
                                <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs font-semibold text-gray-500 uppercase bg-gray-50">
                                    <div className="col-span-5">Jugador</div>
                                    <div className="col-span-3">Monto</div>
                                    <div className="col-span-4 text-right">Acción</div>
                                </div>
                                {cobroModal.jugadores.map(j => (
                                    <div key={j.key} className="grid grid-cols-12 gap-2 px-3 py-2 items-center text-sm">
                                        <div className="col-span-5 min-w-0">
                                            <div className="truncate font-medium text-gray-800">{j.nombre}</div>
                                            <div className="text-xs text-gray-500">{j.socio_id ? 'Socio' : 'Nombre libre'}</div>
                                        </div>
                                        <div className="col-span-3">
                                            <input
                                                type="number"
                                                min="0"
                                                step="100"
                                                value={j.monto}
                                                onChange={e => updateJugador(j.key, { monto: Math.max(0, parseFloat(e.target.value) || 0) })}
                                                className="w-full p-1 border rounded text-sm"
                                                disabled={j.accion === 'nada'}
                                            />
                                        </div>
                                        <div className="col-span-4 flex gap-1 justify-end flex-wrap">
                                            {(['pago', 'debe', 'nada'] as const).map(a => {
                                                const disabled = a === 'debe' && !j.socio_id;
                                                const labels = { pago: 'Pagó', debe: 'Debe', nada: '—' };
                                                const colors: Record<string, string> = {
                                                    pago: 'bg-green-600 text-white border-green-600',
                                                    debe: 'bg-red-600 text-white border-red-600',
                                                    nada: 'bg-gray-500 text-white border-gray-500',
                                                };
                                                return (
                                                    <button
                                                        key={a}
                                                        type="button"
                                                        onClick={() => updateJugador(j.key, { accion: a })}
                                                        disabled={disabled}
                                                        title={disabled ? 'Los nombres libres no pueden tener deuda' : ''}
                                                        className={`px-2 py-1 rounded border text-xs ${j.accion === a ? colors[a] : 'bg-white text-gray-700 hover:bg-gray-50'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                                                    >
                                                        {labels[a]}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="grid grid-cols-3 gap-2 text-sm">
                                <div className="bg-green-50 border border-green-200 rounded p-2">
                                    <div className="text-xs text-green-700 uppercase font-semibold">Cobro a caja</div>
                                    <div className="text-lg font-bold text-green-700">${totalPago.toLocaleString('es-AR')}</div>
                                </div>
                                <div className="bg-red-50 border border-red-200 rounded p-2">
                                    <div className="text-xs text-red-700 uppercase font-semibold">Deudas</div>
                                    <div className="text-lg font-bold text-red-700">${totalDebe.toLocaleString('es-AR')}</div>
                                </div>
                                <div className="bg-gray-50 border border-gray-200 rounded p-2">
                                    <div className="text-xs text-gray-600 uppercase font-semibold">No participa</div>
                                    <div className="text-lg font-bold text-gray-700">${totalNada.toLocaleString('es-AR')}</div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Medio de pago (para el monto cobrado)</label>
                                <select
                                    className="w-full p-2 border rounded"
                                    value={newTurno.medioPago}
                                    onChange={e => setNewTurno(prev => ({ ...prev, medioPago: e.target.value as MedioDePago }))}
                                >
                                    <option value={MedioDePago.EFECTIVO}>Efectivo</option>
                                    <option value={MedioDePago.TRANSFERENCIA}>Transferencia Bancaria</option>
                                </select>
                            </div>

                            <div className="flex justify-end gap-3 pt-2">
                                <button onClick={() => setCobroModal(null)} disabled={cobrando} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 font-medium">Cancelar</button>
                                <button
                                    onClick={confirmCobroJugadores}
                                    disabled={cobrando}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium disabled:bg-gray-400"
                                >
                                    {cobrando ? 'Procesando...' : 'Confirmar y guardar turno'}
                                </button>
                            </div>
                        </div>
                    </Modal>
                );
            })()}

            {/* MODAL DE CONFIRMACIÓN DE ELIMINACIÓN */}
            {turnoToDelete && (
                <Modal isOpen={!!turnoToDelete} onClose={() => { setTurnoToDelete(null); setIsDeleteChoiceOpen(false); }} title="Confirmar Eliminación">
                    <div className="space-y-4">
                        <p className="text-gray-700">
                            ¿Estás seguro de que querés eliminar el turno de <span className="font-bold">{turnoToDelete.socio_titular?.nombre} {turnoToDelete.socio_titular?.apellido}</span>?
                        </p>
                        <div className="bg-red-50 p-3 rounded text-sm text-red-800">
                            <p><strong>Fecha:</strong> {new Date(turnoToDelete.fecha_inicio).toLocaleDateString()}</p>
                            <p><strong>Hora:</strong> {new Date(turnoToDelete.fecha_inicio).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} hs</p>
                        </div>

                        {/* If this turno is from a recurrence, offer choice */}
                        {isDeleteChoiceOpen && ( (turnoToDelete.notas && String(turnoToDelete.notas).includes('[FIJO]')) || (typeof turnoToDelete.id === 'number' && turnoToDelete.id < 0) ) ? (
                            <>
                                <p className="text-sm text-gray-700">Este turno forma parte de una recurrencia. ¿Querés eliminar solo esta aparición o esta y las siguientes?</p>
                                <div className="flex justify-end gap-3 pt-2">
                                    <button onClick={() => { setTurnoToDelete(null); setIsDeleteChoiceOpen(false); }} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 font-medium" disabled={isDeleting}>Cancelar</button>
                                    <button onClick={handleDeleteOnlyThis} className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 font-medium" disabled={isDeleting}>Eliminar solo esta</button>
                                    <button onClick={handleDeleteThisAndFuture} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium" disabled={isDeleting}>{isDeleting ? 'Eliminando...' : 'Eliminar esta y siguientes'}</button>
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="text-sm text-red-600 font-medium">Esta acción no se puede deshacer.</p>
                                <div className="flex justify-end gap-3 pt-2">
                                    <button 
                                        onClick={() => setTurnoToDelete(null)} 
                                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 font-medium"
                                        disabled={isDeleting}
                                    >
                                        Cancelar
                                    </button>
                                    <button 
                                        onClick={confirmDelete} 
                                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium disabled:bg-red-400"
                                        disabled={isDeleting}
                                    >
                                        {isDeleting ? 'Eliminando...' : 'Eliminar Turno'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default TurnosScreen;